/**
 * THE AGENT CHAT PAGE — `/agent`, gated on `agent.chat`.
 *
 * ══ WHAT IS ON THIS SCREEN, AND WHY EACH PIECE IS HERE ═════════════════════
 *
 *   the composer      two fields on the wire, forever (spec 12.4). It is the
 *                     HERO on an empty screen and DOCKS to the foot of the
 *                     thread once a conversation exists.
 *   the starters      chips, not cards — a shortcut, not documentation
 *   the tool timeline the WORK, visible while it happens — not a spinner
 *   the answer        prose as plain text, numbers from the structured result
 *   the provenance    population predicate, freshness stamp, environment
 *   the library rail  saved questions, because the value lands in persisted
 *                     objects rather than in conversation
 *
 * ══ THE ONE PERMISSION WRAPPER ON THIS FEATURE ═════════════════════════════
 *
 * 🔴 THIS ROUTE — and ONLY this route — is wrapped in
 * `<RequirePermission permission="agent.chat">` (in `App.tsx`), because
 * `agent.chat` is the TRANSPORT key. The pinned board and the share page carry
 * NO wrapper at all: the browser cannot know a metric's page keys (there is no
 * client-side catalogue, and `Tile.requires` is a SINGLE string that cannot
 * express a two-key metric), so any client-side page-key gate would be a GUESS
 * — and a guessed key on the surface an operator looks at is weaker than the
 * backend conjunction, in the permissive direction. The backend redacts on
 * every one of these surfaces regardless.
 *
 * ══ ACCESSIBILITY, AND THE HONEST PATTERN ══════════════════════════════════
 *
 * ══ WHY THIS IS A CONVERSATION AND NOT A FORM (2026-08-27) ═════════════════
 *
 * The first version of this screen put six paragraph-sized starter cards above
 * the composer, which pushed the composer BELOW THE FOLD at 900px, and repeated
 * the page subtitle verbatim inside the card beneath it. Measured on DEV that
 * day: the composer's Ask button sat at y=756 in a 784px viewport.
 *
 * Three things follow from treating it as a conversation instead:
 *   - the composer is centred and dominant while the screen is empty, then
 *     steps back to one line at the foot of the thread;
 *   - the question renders as the operator's own message, right-aligned, and
 *     the composer CLEARS on send (it used to keep the text, so the question
 *     appeared twice — once as "You asked", once still sitting in the box);
 *   - the library rail only takes width when it has something in it. An empty
 *     "Nothing saved yet" panel held 22% of the screen permanently.
 *
 * The conversation is `role="log" aria-live="polite"` — never `assertive`,
 * which would flood a screen reader. The RUNNING region is `aria-hidden` while
 * it churns; a single `sr-only role="status"` inside the finished answer
 * announces the outcome once. Icon plus text everywhere, never colour alone.
 */
import { useEffect, useRef, useState } from 'react';
import { Plus, Sparkles } from 'lucide-react';

import { useAgentTurn } from '@/hooks/useAgentTurn';
import { FALLBACK_STARTERS, useAgentArtifacts } from '@/hooks/useAgentArtifacts';

import { AnswerActions } from './components/AnswerActions';
import { Composer } from './components/Composer';
import { Exchange } from './components/Exchange';
import { LibraryRail } from './components/LibraryRail';
import { SavedRunPanel } from './components/SavedRunPanel';
import { EmptyState, ScopeHint } from './components/StateViews';
import { saveBodyFrom } from './save-question';

/** The environment this BUNDLE was built for — the other half of the pill. */
const BUILD_ENVIRONMENT = (import.meta.env as Record<string, string | undefined>).VITE_ENV;

export function AgentChatPage() {
  const turn = useAgentTurn();
  /**
   * 🔴 NEW EXCHANGES LANDED BELOW THE FOLD, AND THE OPERATOR WAS NEVER TAKEN
   * THERE. The transcript is its own scroller (`overflow-y-auto` below), and
   * nothing in this page or in `useAgentTurn` ever set `scrollTop` — so a
   * second question rendered out of sight while the newest thing on screen was
   * the oldest answer. Worse in combination with the blank-on-send remount,
   * which reset `scrollTop` to 0 on every question.
   *
   * Scrolled to the bottom whenever a new exchange joins the record, the live
   * state changes kind, or a question is dispatched — the three moments the
   * newest content moves.
   */
  const logRef = useRef<HTMLElement>(null);
  const artifacts = useAgentArtifacts();
  const [draft, setDraft] = useState('');
  // The saved question open in the panel, if any (spec §16.4 primitive 1).
  const [reopened, setReopened] = useState<{ id: string; label: string } | null>(null);

  // 🔴 CLEAR, never re-seed. This used to `setDraft(question)`, so after
  // sending, the question was on screen TWICE — once as the operator's message
  // and once still sitting in the composer, which reads as "it did not send".
  const ask = (question: string) => {
    setDraft('');
    turn.ask(question);
  };

  const state = turn.state;

  // `state.kind` is still 'empty' during the POST window, so the empty screen
  // must ALSO step aside as soon as a question is in flight — otherwise the
  // chips stay live and clickable while a turn is already being dispatched.
  const conversing = state.kind !== 'empty' || turn.busy || turn.askedQuestion !== null;
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turn.history.length, state.kind, turn.askedQuestion]);
  // The rail earns its width only when it holds something. An empty
  // "Nothing saved yet" panel used to occupy 22% of the screen permanently.
  const railHasContent = artifacts.saved.length > 0;

  return (
    /* h-full, NOT min-h-[calc(100vh-9rem)]. Two defects in one class name.
       `min-h-` is a FLOOR, and a flex child needs a DEFINITE height before
       `flex-1 overflow-y-auto` on a descendant can resolve - so the thread
       never scrolled and the "docked" composer was an ordinary sibling that
       walked down the page as the answer grew. The constant was wrong too:
       AdminLayout.tsx:52-56 is `h-screen` > `<Header/>` +
       `<main className="flex-1 overflow-y-auto p-6">`, so `main` ALREADY has
       a definite height. `h-full` takes it and the magic number disappears
       rather than being corrected to 7rem.

       🔴 THAT WAS ONLY HALF THE FIX, and this comment asserted the whole
       one for a full deploy. `h-full` gives the ROOT a definite height; it does
       NOT let the DESCENDANT scroller shrink. A flex item in a COLUMN has
       `min-height: auto`, whose automatic minimum size is its CONTENT size, so
       the transcript below grew to fit its answer, overflowed every ancestor,
       and pushed the composer back down into `main`'s scroller. Measured live
       on DEV 2026-08-27: the composer sat at y=2556 in an 801px viewport, 1755px
       below the fold, on the very screen where a follow-up is most wanted. Both
       halves are load-bearing: `h-full` here, `min-h-0` on the scroller. */
    <div className="flex h-full flex-col">
      {/* ONE line, ONE control. The subtitle that used to sit here was repeated
          word for word inside the card below it. */}
      <div className="flex items-center justify-between gap-4 border-b pb-3">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Sparkles className="h-4 w-4 text-primary" aria-hidden />
          Analytics
        </h2>
        {conversing && (
          <button
            type="button"
            onClick={turn.reset}
            className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            New question
          </button>
        )}
      </div>

      <div className="flex min-h-0 flex-1 gap-6 pt-5">
        <div className="flex min-w-0 flex-1 flex-col">
          {/* ══ A RE-OPENED SAVED QUESTION — its stored invocation, re-run ══ */}
          {reopened && (
            <div className="max-h-[45vh] shrink-0 overflow-y-auto">
              <SavedRunPanel
                key={reopened.id}
                id={reopened.id}
                label={reopened.label}
                onAsk={(question) => {
                  setReopened(null);
                  ask(question);
                }}
                onClose={() => setReopened(null)}
              />
            </div>
          )}

          {/* ══ EMPTY — the composer is the page ══ */}
          {!conversing && (
            <div className="flex flex-1 flex-col items-center justify-center pb-16">
              <div className="w-full max-w-3xl">
                <h3 className="mb-5 flex items-center gap-2.5 text-2xl font-semibold tracking-tight">
                  <Sparkles className="h-5 w-5 text-primary" aria-hidden />
                  What do you want to know?
                </h3>
                <Composer
                  variant="hero"
                  value={draft}
                  onChange={setDraft}
                  onAsk={ask}
                  onStop={turn.stop}
                  busy={turn.busy || turn.sending}
                />
                <div className="mt-4">
                  <EmptyState
                    starters={artifacts.starters}
                    degraded={artifacts.startersDegraded}
                    loading={artifacts.startersLoading}
                    fallback={FALLBACK_STARTERS}
                    onPick={ask}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ══ CONVERSATION ══ */}
          {conversing && (
            <>
              <section
                ref={logRef}
                role="log"
                aria-live="polite"
                aria-label="Questions asked this session"
                /* tabIndex 0: a scrollable region containing no focusable
                   element cannot be reached or scrolled by keyboard. WCAG 2.1.1. */
                tabIndex={0}
                /* min-h-0: WITHOUT IT `overflow-y-auto` NEVER ENGAGES. See the
                   block comment at the top of this return.

                   🔴 `relative` IS LOAD-BEARING, AND IT IS THE ACCESSIBILITY
                   MARKUP THAT MAKES IT SO. Tailwind's `.sr-only` is
                   `position: absolute`. With no positioned ancestor, every
                   screen-reader caption inside this thread resolves against the
                   INITIAL CONTAINING BLOCK — the document — and lands at a
                   document coordinate as far down as the thread is long.
                   `document.documentElement.scrollHeight` then grows with them
                   even though this scroller clips its own content correctly.

                   Measured on DEV with three answers on screen:
                     without `relative`  documentElement.scrollHeight = 5072px
                     with    `relative`  documentElement.scrollHeight =  745px
                   against a 745px viewport. The whole shell is `h-screen`, so
                   that surplus was 4327px of PAGE THAT SCROLLS INTO NOTHING:
                   scroll the window and the content — which lives in here and
                   does not move — is simply gone, replaced by empty
                   background. It reads as a broken page, and it got worse with
                   every answer.

                   The `sr-only` captions are correct and stay. This gives them
                   a containing block so they are clipped here, where they
                   belong, instead of stretching the document. */
                className="relative mx-auto w-full max-w-3xl min-h-0 flex-1 space-y-8 overflow-y-auto focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {/* 🔴 THE SESSION RECORD, oldest first. Client-side only — the
                    transport is two fields forever and the model receives none
                    of this. What it fixes: `ask()` used to destroy the previous
                    answer AT DISPATCH, so a second question wiped the first
                    immediately and left 12-19 seconds of nothing in its place,
                    with no undo and no scrollback. Read-only: a finished
                    exchange carries no actions, because pinning needs the turn
                    id of the turn being pinned. */}
                {turn.history.map((past, index) => (
                  <Exchange
                    key={index}
                    question={past.question}
                    state={past.state}
                    buildEnvironment={BUILD_ENVIRONMENT}
                  />
                ))}

                <Exchange
                  question={turn.askedQuestion}
                  state={state}
                  buildEnvironment={BUILD_ENVIRONMENT}
                  egressProfile={turn.poll?.egress_profile ?? ''}
                  onPick={ask}
                  onReset={turn.reset}
                  onRetry={() => turn.ask(turn.askedQuestion ?? draft)}
                  actions={
                    state.kind === 'answered' ? (
                      <AnswerActions
                        artifacts={artifacts}
                        turnId={turn.turnId}
                        saveBody={saveBodyFrom(state.result.results, new Date().toISOString().slice(0, 10))}
                        defaultLabel={turn.askedQuestion ?? ''}
                        rowLevelAnswer={state.result.results.some((record) => record.tool === 'database_query')}
                      />
                    ) : undefined
                  }
                />
              </section>

              <div className="mx-auto w-full max-w-3xl pt-4">
                <Composer
                  variant="docked"
                  value={draft}
                  onChange={setDraft}
                  onAsk={ask}
                  onStop={turn.stop}
                  busy={turn.busy || turn.sending}
                />
                <ScopeHint />
              </div>
            </>
          )}
        </div>

        {/* The rail takes width only when it has something in it. */}
        {railHasContent && (
          <div className="hidden w-72 shrink-0 lg:block">
            <LibraryRail artifacts={artifacts} onReopen={setReopened} />
          </div>
        )}
      </div>
    </div>
  );
}

export default AgentChatPage;
